<script>
  import { QRCode } from '@client/components/qr-code';

  import { UserSilhouette } from '@client/pages/brandkit/components';
  import { getBackgroundStyle, parseImage, parseQrCode } from '@client/pages/brandkit/utils';

  import { getContrastColor } from '@client/utils/color';

  import Resize from './resize.svelte';

  export let user = {};
  export let showUser = false;
  export let width = 0;
  export let height = 0;

  export let background;
  export let blur = 0;
  export let brightness = 0;
  export let foreground;
  export let cells = Array(9)
    .fill(null)
    .map(() => []);
  export let dropTarget = null;
  export let padding = [0, 0];

  /** @type {(index: number, lineNumber: number) => void} */
  export let onDelete = () => {};
  export let onDragEnd = () => {};
  export let onDragOver = () => {};
  export let onDragOverItem = () => {};
  export let onDragStart = () => {};
  export let onDrop = () => {};

  /** @type {(index: number, lineNumber: number, tag: string) => void} */
  export let onScale = (index, lineNumber, tag) => {};

  /** @type {(index: number, lineNumber: number, type: string) => void} */
  export let onSelect = () => {};

  let innerWidth = 0;
  let innerHeight = 0;
  let scale = 1;

  const processCell = (value, user) => {
    return value.replace(
      /\{\{\s*(\w+)\s*(?:\|\s*default:\s*(?:"([^"]*)"|(\w+)))?\s*\}\}/g,
      (match, key, literalDefault, fieldDefault) => {
        if (user && user[key] !== undefined) return user[key];
        if (literalDefault !== undefined) return literalDefault;
        if (fieldDefault !== undefined) return user[fieldDefault] ?? match;
        return match;
      }
    );
  };

  const handleDragStart = (e, item, index, lineNumber) => {
    onDragStart(e, item, index, lineNumber);
  };

  /**
   * @param {string} src
   * @param {number} scale
   * @param {number} index
   * @param {number} lineNumber
   */
  const handleImageScale = (src, scale, index, lineNumber) => {
    if (Number.isFinite(Number(scale))) {
      const img = `{{ "${src}" | img: ${scale.toFixed(3)} }}`;
      onScale(index, lineNumber, img);
    }
  };

  const handleItemDragOver = (e, cellIndex, lineNumber) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const midY = rect.top + rect.height / 2;
    const insertAfter = e.clientY > midY;
    onDragOverItem(cellIndex, lineNumber, insertAfter);
  };

  /**
   * @param {string} data
   * @param {number} scale
   * @param {number} index
   * @param {number} lineNumber
   */
  const handleQrScale = (data, scale, index, lineNumber) => {
    if (Number.isFinite(Number(scale))) {
      const qr = `{{ "${data}" | qr_code: ${scale.toFixed(3)} }}`;
      onScale(index, lineNumber, qr);
    }
  };

  const handleSelect = (e, index, lineNumber) => {
    e.currentTarget.focus();

    const item = cells[index][lineNumber];
    selectedIndices = { cell: index, line: lineNumber };

    const isImage = !!parseImage(item)[0];
    const isQrCode = !!parseQrCode(item)[0];

    if (item.startsWith('http') || isImage) {
      onSelect(index, lineNumber, 'images');
    } else if (isQrCode) {
      onSelect(index, lineNumber, 'qr-codes');
    } else {
      onSelect(index, lineNumber, 'fields');
    }
  };

  const handleDelete = (e, index, lineNumber) => {
    if (e.key === 'Delete' || e.key === 'Backspace') {
      onDelete(index, lineNumber);
    }
  };

  const getObjectPosition = (index) => {
    if (index < 0 || index > 8 || index === 4) {
      return 'center';
    }

    const columns = ['left', 'center', 'right'];
    const rows = ['top', 'center', 'bottom'];

    const column = index % 3;
    const row = Math.floor(index / 3);

    return `${columns[column]} ${rows[row]}`;
  };

  $: filter =
    [blur && `blur(${blur}px)`, brightness && `brightness(${brightness})`].filter(Boolean).join(' ') || undefined;

  $: scale = Math.min(1, innerWidth / width, innerHeight / height);

  // Track the currently selected item to show handles
  let selectedIndices = { cell: -1, line: -1 };

  // Helper to check selection
  $: isSelected = (idx, line) => selectedIndices.cell === idx && selectedIndices.line === line;
</script>

<div class="viewport" bind:clientWidth={innerWidth} bind:clientHeight={innerHeight}>
  <div
    class="grid"
    style:background={getBackgroundStyle(background)}
    style:height="{height}px"
    style:transform="scale({scale})"
    style:width="{width}px"
  >
    <div
      class="cells"
      style:color={foreground}
      style:padding="{padding[0]}px {padding[1]}px"
      style:text-shadow="0 0 24px {getContrastColor(foreground)}80"
      style:backdrop-filter={filter}
    >
      {#each cells as cell, index}
        <div class="cell" role="region" on:dragover={onDragOver} on:drop={(e) => onDrop(e, index)}>
          {#each cell as item, lineNumber}
            {@const [src, scale] = parseImage(item) || (item.startsWith('http') && item)}
            {@const [qr, qrScale] = parseQrCode(item)}
            {#if src}
              <Resize
                selected={isSelected(index, lineNumber)}
                objectPosition={getObjectPosition(index)}
                {scale}
                on:click={(e) => handleSelect(e, index, lineNumber)}
                on:scale={(e) => handleImageScale(src, e.detail.scale, index, lineNumber)}
              >
                <!-- svelte-ignore a11y-no-noninteractive-element-interactions a11y-no-noninteractive-tabindex -->
                <img
                  alt={item}
                  class:insert-before={dropTarget?.cellIndex === index &&
                    dropTarget?.lineNumber === lineNumber &&
                    !dropTarget?.insertAfter}
                  class:insert-after={dropTarget?.cellIndex === index &&
                    dropTarget?.lineNumber === lineNumber &&
                    dropTarget?.insertAfter}
                  draggable="true"
                  role="listitem"
                  src={processCell(src, user)}
                  tabindex="0"
                  on:click={(e) => handleSelect(e, index, lineNumber)}
                  on:dragend={onDragEnd}
                  on:dragover={(e) => handleItemDragOver(e, index, lineNumber)}
                  on:dragstart={(e) => handleDragStart(e, item, index, lineNumber)}
                  on:keydown={(e) => handleDelete(e, index, lineNumber)}
                />
              </Resize>
            {:else if qr}
              <Resize
                selected={isSelected(index, lineNumber)}
                objectPosition={getObjectPosition(index)}
                scale={qrScale}
                on:click={(e) => handleSelect(e, index, lineNumber)}
                on:scale={(e) => handleQrScale(qr, e.detail.scale, index, lineNumber)}
              >
                <!-- svelte-ignore a11y-no-noninteractive-element-interactions a11y-no-noninteractive-tabindex -->
                <QRCode
                  alt={item}
                  draggable="true"
                  role="listitem"
                  data={qr}
                  style="
                  display: block;
                  height: 100%;
                  max-height: 100%;
                  max-width: 100%;
                  object-fit: contain;
                  object-position: {getObjectPosition(index)};
                  width: 100%;"
                  tabindex="0"
                  on:click={(e) => handleSelect(e, index, lineNumber)}
                  on:dragend={onDragEnd}
                  on:dragover={(e) => handleItemDragOver(e, index, lineNumber)}
                  on:dragstart={(e) => handleDragStart(e, item, index, lineNumber)}
                  on:keydown={(e) => handleDelete(e, index, lineNumber)}
                />
              </Resize>
            {:else}
              <svelte:element this={`h${Math.min(lineNumber + 1, 6)}`}>
                <!-- svelte-ignore a11y-no-noninteractive-element-interactions a11y-no-noninteractive-tabindex -->
                <span
                  class:insert-before={dropTarget?.cellIndex === index &&
                    dropTarget?.lineNumber === lineNumber &&
                    !dropTarget?.insertAfter}
                  class:insert-after={dropTarget?.cellIndex === index &&
                    dropTarget?.lineNumber === lineNumber &&
                    dropTarget?.insertAfter}
                  draggable="true"
                  role="listitem"
                  tabindex="0"
                  on:click={(e) => handleSelect(e, index, lineNumber)}
                  on:dragend={onDragEnd}
                  on:dragover={(e) => handleItemDragOver(e, index, lineNumber)}
                  on:dragstart={(e) => handleDragStart(e, item, index, lineNumber)}
                  on:keydown={(e) => handleDelete(e, index, lineNumber)}
                >
                  {processCell(item, user)}
                </span>
              </svelte:element>
            {/if}
          {/each}
        </div>
      {/each}

      {#if showUser}
        <UserSilhouette opacity={0.67} />
      {/if}
    </div>
  </div>
</div>

<style lang="scss">
  .viewport {
    display: grid;
    overflow: hidden;
  }

  .grid {
    height: 100%;
    transform-origin: center left;
    width: 100%;
  }

  .cells {
    background-color: transparent;
    border: 1px solid black;
    display: grid;
    grid-template-columns: repeat(3, calc(100% / 3));
    grid-template-rows: repeat(3, calc(100% / 3));
    height: 100%;
    transform-origin: center left;
    transition: padding 200ms ease-in-out;
    width: 100%;

    .cell {
      border: 2px solid transparent;
      display: flex;
      flex-direction: column;
      flex-wrap: wrap;
      gap: 8px;
      outline-offset: -2px;
      outline: 2px dashed transparent;
      padding: 1rem;
      padding: 8px;

      // Column 1
      &:nth-child(3n + 1) {
        align-items: flex-start;
        padding-left: 7.5%;
      }

      // Column 2
      &:nth-child(3n + 2) {
        align-items: flex-start;

        > :global(*) {
          position: relative;
          left: 50%;
          transform: translateX(-50%);
        }
      }

      // Column 3
      &:nth-child(3n) {
        align-items: flex-start;
        padding-right: 7.5%;

        > :global(*) {
          position: relative;
          left: 100%;
          transform: translateX(-100%);
        }
      }

      // Row 1
      &:nth-child(-n + 3) {
        justify-content: flex-start;
        padding-top: 7.5%;
      }

      // Row 2
      &:nth-child(n + 4):nth-child(-n + 6) {
        justify-content: center;
        padding-bottom: 4.25%;
        padding-top: 4.25%;
      }

      // Row 3
      &:nth-child(n + 7) {
        justify-content: flex-end;
        padding-bottom: 7.5%;
      }

      &:nth-child(1) {
        img {
          object-position: left top;
        }
      }

      &:nth-child(2) {
        img {
          object-position: center top;
        }
      }

      &:nth-child(3) {
        img {
          object-position: right top;
        }
      }

      &:nth-child(4) {
        img {
          object-position: left center;
        }
      }

      &:nth-child(5) {
        img {
          object-position: center;
        }
      }

      &:nth-child(6) {
        img {
          object-position: right center;
        }
      }

      &:nth-child(7) {
        img {
          object-position: left bottom;
        }
      }

      &:nth-child(8) {
        img {
          object-position: center bottom;
        }
      }

      &:nth-child(9) {
        img {
          object-position: right bottom;
        }
      }
    }

    &:hover div {
      border-bottom: 1px dashed rgba(0, 0, 0, 0.5);
      border-right: 1px dashed rgba(0, 0, 0, 0.5);
      border-top: 1px dashed rgba(255, 255, 255, 0.5);
      border-left: 1px dashed rgba(255, 255, 255, 0.5);
    }

    h1 {
      font-size: 4rem;
    }
    h2 {
      font-size: 3rem;
    }
    h3 {
      font-size: 2.33rem;
    }
    h4 {
      font-size: 2rem;
    }
    h5 {
      font-size: 1.67rem;
    }
    h6 {
      font-size: 1.5rem;
    }

    img {
      max-width: 100%;
      max-height: 100%;
      width: 100%;
      height: 100%;
      object-fit: contain;
      display: block;
    }

    img:focus,
    span:focus {
      outline: auto; /* browser's default focus style */
    }

    .insert-before {
      border-top: 2px solid currentColor;
    }

    .insert-after {
      border-bottom: 2px solid currentColor;
    }
  }
</style>
